import type { IUseGetSocialOfferPrescriptionFilledResponse, TUseGetSocialApplyByStatusResponse } from "@api";
import { PartnerDecision, SocialApplyStatus } from "@bbj-types/socialApply/enum";
import type { ObjectId } from "@bbj-types/utils";
import { Stack, Typography } from "@mui/material";
import { ListVirtualizedV2 } from "@styleguide";
import { useLayoutEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useCandidatesListContext } from "../../context";
import { AccompaniementPopover } from "./accompaniementPopover";
import { PrescriptionCandidatesCard } from "./card";
import { PrescriptionsHeader } from "./header";
import { FupModal } from "./modal";

interface IPrescriptionsProps {
  data: TUseGetSocialApplyByStatusResponse<SocialApplyStatus.PRESCRIPTION_FILLED>["data"];
  loading?: boolean;
}

export interface IPopoverState {
  anchorEl: HTMLElement | null;
  otherIntegratedSocialOffers: IUseGetSocialOfferPrescriptionFilledResponse["otherIntegratedSocialOffers"];
  accompaniement: string;
}

export const Prescriptions = ({ data, loading = false }: IPrescriptionsProps) => {
  const { changeTab } = useCandidatesListContext();
  const [socialApplyIdToComplete, setSocialApplyIdToComplete] = useState<ObjectId | undefined>(undefined);
  const [accompaniementPopoverState, setAccompaniementPopoverState] = useState<IPopoverState>({
    anchorEl: null,
    otherIntegratedSocialOffers: [],
    accompaniement: "",
  });
  const [searchParams, setSearchParams] = useSearchParams();

  useLayoutEffect(() => {
    const socialApplyId = searchParams.get("socialApplyId");
    if (socialApplyId) {
      setSocialApplyIdToComplete(socialApplyId);
    } else {
      setSocialApplyIdToComplete(undefined);
    }
  }, [searchParams]);

  return (
    <Stack sx={{ height: 900, overflowX: "hidden", overflowY: "auto", flex: "auto", pl: 6, pr: 8, gap: 2 }}>
      <Stack sx={{ flexDirection: "row", alignItems: "center", gap: 2, minHeight: 60 }}>
        <Typography
          variant="subtitle1SemiBold"
          sx={{
            color: "primary.main",
            ml: 2,
          }}
        >
          {loading ? "prescriptions" : `${data.length} prescription${data.length <= 1 ? "" : "s"}`}
        </Typography>
      </Stack>
      <ListVirtualizedV2
        data={data}
        itemContent={(index, item) => (
          <PrescriptionCandidatesCard
            key={index}
            data={item}
            setAccompaniementPopoverState={setAccompaniementPopoverState}
            completePrescription={() =>
              setSearchParams((prev) => {
                prev.set("socialApplyId", item._id);
                return prev;
              })
            }
          />
        )}
        components={{
          Header: PrescriptionsHeader,
        }}
      />
      <AccompaniementPopover
        anchorEl={accompaniementPopoverState.anchorEl}
        otherIntegratedSocialOffers={accompaniementPopoverState.otherIntegratedSocialOffers}
        accompaniement={accompaniementPopoverState.accompaniement}
        setAccompaniementPopoverState={setAccompaniementPopoverState}
      />
      {socialApplyIdToComplete && (
        <FupModal
          onClose={(result) => {
            if (result) {
              window.publish("feedback", {
                severity: "success",
                message:
                  "Votre réponse à la prescription a bien été transmise au référent. Retrouvez-là dans la page ci-dessous.",
              });
              changeTab(
                result.decision === PartnerDecision.INTEGRATED
                  ? SocialApplyStatus.INTEGRATED_CANDIDATE
                  : SocialApplyStatus.REFUSED_STRUCTURE
              );
            } else {
              setSearchParams((prev) => {
                prev.delete("socialApplyId");
                return prev;
              });
            }
          }}
          socialApplyId={socialApplyIdToComplete}
        />
      )}
    </Stack>
  );
};
